import { SettingsLayout } from "@/components/settings-layout";
import { trpc } from "@/lib/trpc";
import {
	NOTIFICATION_CATEGORIES,
	NOTIFICATION_EVENT_DESCRIPTIONS,
	NOTIFICATION_EVENT_LABELS,
	type NotificationEvent,
} from "@payrollmart-invoice/shared";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/settings/notifications")({
	component: NotificationsSettingsPage,
});

type PrefMap = Record<NotificationEvent, boolean>;

function seedLocalFrom(rows: { eventType: string; emailEnabled: boolean }[]): PrefMap {
	const next = {} as PrefMap;
	for (const row of rows) next[row.eventType as NotificationEvent] = row.emailEnabled;
	return next;
}

function NotificationsSettingsPage() {
	const utils = trpc.useUtils();
	const prefsQuery = trpc.notifications.getPreferences.useQuery();

	const update = trpc.notifications.updatePreferences.useMutation({
		onSuccess: async (fresh) => {
			// Server returns the canonical list; seed the cache and our local
			// state directly so a save is reflected without a refetch round-trip.
			utils.notifications.getPreferences.setData(undefined, fresh);
			setLocal(seedLocalFrom(fresh));
			// Onboarding step unlocks the first time prefs are saved — flush
			// that query so /onboarding reflects it on the next visit.
			await utils.profile.onboardingStatus.invalidate();
			toast.success("Preferences saved");
		},
		onError: (err) => toast.error(err.message),
	});

	const [local, setLocal] = useState<PrefMap | null>(null);

	// Seed local editable state from server once data arrives.
	useEffect(() => {
		if (!prefsQuery.data || local) return;
		setLocal(seedLocalFrom(prefsQuery.data));
	}, [prefsQuery.data, local]);

	if (prefsQuery.isPending || !local) {
		return (
			<SettingsLayout>
				<div className="h-6 w-64 animate-pulse rounded bg-[#f3f4f6]" />
				<div className="h-40 w-full animate-pulse rounded bg-[#f3f4f6]" />
			</SettingsLayout>
		);
	}

	const dirty =
		prefsQuery.data?.some((row) => {
			const current = local[row.eventType as NotificationEvent];
			return current !== row.emailEnabled;
		}) ?? false;

	function toggle(event: NotificationEvent) {
		setLocal((prev) => (prev ? { ...prev, [event]: !prev[event] } : prev));
	}

	function handleSave() {
		if (!local) return;
		const updates = (Object.entries(local) as [NotificationEvent, boolean][]).map(
			([eventType, emailEnabled]) => ({ eventType, emailEnabled }),
		);
		update.mutate({ updates });
	}

	return (
		<SettingsLayout>
			<div>
				<h2 className="text-lg font-semibold text-[#1e1e20]">Notification preferences</h2>
				<p className="mt-1 text-sm text-[#6b7280]">
					Choose which events we email you about. Changes apply to future events only — your
					notification history isn't touched.
				</p>
			</div>

			{(
				Object.entries(NOTIFICATION_CATEGORIES) as [
					keyof typeof NOTIFICATION_CATEGORIES,
					(typeof NOTIFICATION_CATEGORIES)[keyof typeof NOTIFICATION_CATEGORIES],
				][]
			).map(([key, category]) => (
				<section key={key} className="rounded-lg border border-[#e5e7eb] bg-white">
					<div className="border-b border-[#e5e7eb] px-6 py-3">
						<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">
							{category.label}
						</h2>
					</div>
					<ul className="divide-y divide-[#f3f4f6]">
						{category.events.map((event) => (
							<li key={event} className="flex items-start justify-between gap-4 px-6 py-4">
								<div className="flex-1">
									<p className="text-sm font-medium text-[#1e1e20]">
										{NOTIFICATION_EVENT_LABELS[event]}
									</p>
									<p className="mt-0.5 text-xs text-[#6b7280]">
										{NOTIFICATION_EVENT_DESCRIPTIONS[event]}
									</p>
								</div>
								<Toggle
									checked={local[event]}
									onChange={() => toggle(event)}
									label={NOTIFICATION_EVENT_LABELS[event]}
								/>
							</li>
						))}
					</ul>
				</section>
			))}

			<div className="sticky bottom-4 flex items-center justify-end gap-3 rounded-lg border border-[#e5e7eb] bg-white px-6 py-3 shadow-sm">
				<span className="text-xs text-[#6b7280]">
					{dirty ? "Unsaved changes" : "All changes saved"}
				</span>
				<button
					type="button"
					onClick={handleSave}
					disabled={!dirty || update.isPending}
					className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
				>
					{update.isPending ? "Saving…" : "Save preferences"}
				</button>
			</div>
		</SettingsLayout>
	);
}

function Toggle({
	checked,
	onChange,
	label,
}: {
	checked: boolean;
	onChange: () => void;
	label: string;
}) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			aria-label={`Toggle ${label}`}
			onClick={onChange}
			className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 ${
				checked ? "bg-[#16a34a]" : "bg-[#d1d5db]"
			}`}
		>
			<span
				className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition-transform ${
					checked ? "translate-x-4" : "translate-x-0"
				}`}
			/>
		</button>
	);
}
